import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";

import { Container } from "@pixi/react";
import CoinCollectAnimator from "./CoinCollectAnimator";
import { Settings } from "../../settings";

/**
 * RoyalCollectCollectionLayer
 *
 * High-zIndex container that hosts coin-to-royal travel animations.
 * Sits above all other Royal Collect layers (overlay, reelset, frame).
 *
 * Creates a CoinCollectAnimator and exposes collectCoins() via imperative handle.
 * The ControlContext triggers collection; this component just provides the
 * container and animator lifecycle.
 */
const RoyalCollectCollectionLayer = forwardRef(
  ({ symbolWidth, symbolHeight }, ref) => {
    const containerRef = useRef(null);
    const animatorRef = useRef(null);

    // Initialize animator once container is ready
    useEffect(() => {
      if (!containerRef.current || animatorRef.current) return;

      animatorRef.current = new CoinCollectAnimator(
        containerRef.current,
        symbolWidth,
        symbolHeight,
      );

      return () => {
        if (animatorRef.current) {
          animatorRef.current.destroy();
          animatorRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update dimensions on resize
    useEffect(() => {
      if (animatorRef.current) {
        animatorRef.current.resize(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight]);

    // ── Exposed API ────────────────────────────────────────────────────────

    /**
     * Trigger sequential coin collection animation.
     *
     * @param {Array} coins - List of coins with spine refs and world coordinates
     * @param {{x, y}} royalPosition - Local coordinates of royal symbol
     * @param {object} options - { onCoinCollected, onAllComplete }
     * @returns {Promise<void>}
     */
    const collectCoins = useCallback(
      (coins, royalPosition, options = {}) => {
        if (!animatorRef.current) {
          options.onAllComplete?.();
          return Promise.resolve();
        }
        return animatorRef.current.collectCoins(coins, royalPosition, options);
      },
      [],
    );

    /**
     * Trigger royal symbol collection animation.
     *
     * @param {Array} royals - List of royals with spine refs, positions, and arcDirection
     * @param {{x, y}} counterPosition - Local coordinates of respin counter
     * @param {object} options - { onRoyalCollected, onAllComplete, speedConfig }
     * @returns {Promise<void>}
     */
    const collectRoyals = useCallback(
      (royals, counterPosition, options = {}) => {
        if (!animatorRef.current) {
          options.onAllComplete?.();
          return Promise.resolve();
        }
        return animatorRef.current.collectRoyals(royals, counterPosition, options);
      },
      [],
    );

    const cancel = useCallback(() => {
      animatorRef.current?.cancel();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        collectCoins,
        collectRoyals,
        cancel,
      }),
      [collectCoins, collectRoyals, cancel],
    );

    // Top-aligned: container origin sits at the top edge of the grid.
    // ReelSet uses center-based Y, so we subtract symbolHeight/2 to align to top.
    const containerY = useMemo(
      () => -symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5) - symbolHeight / 2,
      [symbolHeight],
    );

    return (
      <Container
        ref={containerRef}
        name="royal_collect_collection_layer"
        y={containerY}
        zIndex={20}
        sortableChildren
      />
    );
  },
);

RoyalCollectCollectionLayer.displayName = "RoyalCollectCollectionLayer";

export default RoyalCollectCollectionLayer;
